import { getImageCode, upload } from "@/apis/common";
import { addPayment } from "@/apis/user";
import { DialogLocal } from "@/components/Dialog";
import { Payments } from "@/constants";
import { usePaymentsStore } from "@/stores/payments";
import { Bank } from "@/types/Api/User/PaymentType/getBankInfo";
import tools from "@/utils/tool";
import { useLockFn, useMount, useRequest, useSetState } from "ahooks";
import { AutoCenter, Form, Toast } from "antd-mobile";
import { useMemo } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
export type AddPaymentData = {
  type: Payments;
  realName?: string;
  bankNo?: string;
  bankId?: string | number;
  bankBranch?: string;
  provinceId?: string | number;
  cityId?: string | number;
  securePassword?: string;
  alipayAccount?: string;
  qrContent?: string;
  receiptCode?: string;
  wxRealname?: string;
  code?: string;
  verifyUniqid?: string;
  usdtAddress?: string;
  isBusiness?: '1' | '2' | undefined;
  digitalAddress?: string;
};
type Exrea = any;
const validateBankForm = (data: AddPaymentData, extra?: Exrea) => {
  if (extra.isMerchant && !data.realName) return "请输入持卡人姓名";
  if (!data.bankId) return "请选择银行";
  if (!data.cityId) return "请选择开户所在地";
  if (!data.bankNo) return "请输入银行卡号";
  if (data.bankNo.length < 10 || data.bankNo.length > 20) return "银行卡号为10-20位数字";
  if (!/^\d{6}$/.test(`${data.securePassword}`)) return "请输入6数字组成的资金密码";
  if (!extra.isMerchant && extra.banks && extra.banks.findIndex((item: Bank) => item.bankNo === data.bankNo) > -1) {
    return "该银行卡号已绑定";
  }
};
const validateAlipayForm = (data: AddPaymentData, extra?: Exrea) => {
  if (!data.alipayAccount) return "请输入支付宝账号";
  if (extra.isMerchant && !data.realName) return "请输入真实姓名";
  if (!data.cityId) return "请输入所在地区";
  if (!data.receiptCode || `${data.receiptCode}`.length <= 0) return "请上传收款码";
  if (!data.securePassword || !/^\d{6}$/.test(`${data.securePassword}`)) return "请输入6数字组成的资金密码";
};
const validateWechatForm = (data: AddPaymentData) => {
  if (!data.wxRealname) return "请输入微信姓名";
  if (!data.cityId) return "请输入所在地区";
  if (!data.receiptCode || `${data.receiptCode}`.length <= 0) return "请上传收款码";
  if (!data.securePassword || !/^\d{6}$/.test(`${data.securePassword}`)) return "请输入6数字组成的资金密码";
};
const validateUSDTForm = (data: AddPaymentData) => {
  if (!data.cityId) return "请输入所在地区";
  if (!data.usdtAddress) return "请输入USDT收款地址";
  if (!data.securePassword || !/^\d{6}$/.test(`${data.securePassword}`)) return "请输入6数字组成的资金密码";
};
const validateDigitalRmbForm = (data: AddPaymentData) => {
  if (!data.realName) return "请输入姓名";
  if (!data.cityId) return "请输入所在地区";
  if (!data.digitalAddress) return "请输入数字人民币收款地址";
  if (!data.securePassword || !/^\d{6}$/.test(`${data.securePassword}`)) return "请输入6数字组成的资金密码";
};
export const validateData = {
  [`${Payments.BANK}`]: validateBankForm,
  [`${Payments.ALI}`]: validateAlipayForm,
  [`${Payments.WE_CHAT}`]: validateWechatForm,
  [`${Payments.CRYPTO_COIN}`]: validateUSDTForm,
  [`${Payments.DIGITAL_RMB}`]: validateDigitalRmbForm
};
export const useSetup = () => {
  const [form] = Form.useForm();
  const {
    type = Payments.BANK
  } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [updatePayments, formatPayments] = usePaymentsStore(state => [state.updatePayments, state.formatPayments]);
  const isMerchant = useMemo(() => {
    return searchParams.get('m') === '1' || searchParams.get('m') === '2';
  }, [searchParams]);
  const banks = useMemo(() => formatPayments[(Number(type) as Payments)]?.list ?? [], [formatPayments, type]);
  const [state, setState] = useSetState({
    verifyUniqid: "",
    verifyCode: ""
  });
  const uploadQRcode = useLockFn(async file => {
    const formData = new FormData();
    formData.append("imgFile", file);
    formData.append("type", "other");
    const res = await upload(formData);
    Toast.show(res.msg);
    return res.data?.name;
  });
  const onSubmit = () => {
    const values = form.getFieldsValue();
    const data: AddPaymentData = {
      type: ((type as unknown) as Payments),
      bankNo: values.bankNo,
      bankId: values.bank?.[0]?.value,
      bankBranch: values.bankBranch,
      provinceId: values.area?.[0]?.value,
      cityId: values.area?.[1]?.value,
      securePassword: values.securePassword,
      alipayAccount: values.alipayAccount,
      qrContent: values.receiptCode?.decodeQRcode,
      receiptCode: values.receiptCode?.image,
      wxRealname: values.wxRealname,
      code: values.code,
      verifyUniqid: state.verifyUniqid,
      usdtAddress: values.usdtAddress,
      digitalAddress: values.digitalAddress
    };
    let valid: string | undefined;
    if (Payments.BANK === Number(type)) {
      if (isMerchant) {
        data.realName = values.realName;
      }
      valid = validateBankForm(data, {
        banks,
        isMerchant
      });
    }
    if (Payments.ALI === Number(type)) {
      if (isMerchant) {
        data.realName = values.realName;
      }
      valid = validateAlipayForm(data, {
        isMerchant
      });
    }
    if (Payments.WE_CHAT === Number(type)) {
      valid = validateWechatForm(data);
    }
    if (Payments.CRYPTO_COIN === Number(type)) {
      valid = validateUSDTForm(data);
    }
    if (Payments.DIGITAL_RMB === Number(type)) {
      data.realName = values.realName;
      valid = validateDigitalRmbForm(data);
    }
    if (tools.isString(valid)) return Toast.show(valid);
    add.run(data);
  };
  const add = useRequest<boolean, [AddPaymentData]>(async (data: AddPaymentData) => {
    if (searchParams.get('m') === '1') {
      data.isBusiness = '1';
    }
    if (searchParams.get('m') === '2') {
      data.isBusiness = '2';
    }
    const result = await addPayment(data);
    if (result.ok) {
      updatePayments();
      if (Payments.ALI === Number(data.type)) {
        DialogLocal({
          title: "提交成功",
          confirmText: "我知道了",
          content: <AutoCenter>
                绑定支付宝账号需后台审核通过后才可以正常使用。审核部门会尽快处理，请耐心等待，感谢你的支持！
              </AutoCenter>,
          onConfirm: () => {
            navigate(-1);
          }
        });
      } else {
        navigate(-1);
        Toast.show(result.msg);
      }
    } else {
      flushCodeImage();
      Toast.show(result.msg);
    }
    return result.ok;
  }, {
    manual: true
  });
  const flushCodeImage = async () => {
    const response = await getImageCode({
      codeType: "addCard"
    });
    if (response.ok) {
      setState({
        verifyCode: response.data?.verifyCode || "",
        verifyUniqid: response.data?.verifyUniqid || ""
      });
    } else {
      // TODO: 请求失败的处理
    }
  };
  useMount(() => {
    flushCodeImage();
  });
  return {
    type,
    form,
    onSubmit,
    add,
    uploadQRcode,
    flushCodeImage,
    state,
    isMerchant
  };
};